import { useCallback, useRef, useState } from "react"

import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { CSSTransition } from "react-transition-group"

import { isNullOrUndefined } from "$/utils/gates"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TUserState, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { useSimulabContext } from "@/entities/simulab"
import { useSendSimulabFeedback } from "@/entities/simulab/api"
import { WarningMessage } from "@/shared/WarningMessage"
import { DynamicHeightTransition } from "@/shared/lib/components/DynamicHeightTransition"
import Modal from "@/shared/modal"

import "../styles.scss"

import { SimulabFeedbackForm } from "./SimulabFeedbackForm"

export const SimulabFeedbackModal = () => {
    const { t } = useTranslation()
    const { user }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()
    const { report, setReportData } = useSimulabContext()
    const sendFeedback = useSendSimulabFeedback()
    const [feedbackSent, setFeedbackSent] = useState(false)
    const [showFeedbackAnimation, setShowFeedbackAnimation] = useState(false)
    const [error, setError] = useState(null)

    const onClose = useCallback(() => {
        setReportData(null)
    }, [setReportData])

    const onSubmit = useCallback(values => {
        const submitData = {
            text: values.feedback,
            severity: values.severity,
            context: report.type
        }

        if ("message_id" in report) {
            // @ts-expect-error weird field here
            submitData.message_id = report.message_id
        }

        sendFeedback.mutate(submitData, {
            onSuccess() {
                setFeedbackSent(true)
            },
            onError(error: AxiosError) {
                setError(getErrorMessages(error))
            }
        })
    }, [])

    const handleFormExit = () => {
        if (feedbackSent) {
            setTimeout(() => {
                setShowFeedbackAnimation(true)
            }, 600)
        }
    }

    const onDoneFeedbackClick = useCallback(() => {
        setReportData(null)
    }, [setReportData])

    const feedbackModalRef = useRef(null)
    const feedbackResultRef = useRef(null)

    return (
        <Modal
            show={!isNullOrUndefined(report)}
            onClose={onClose}
            modalBodyClass="simulab-report-modal"
            headerContent={
                <Typography
                    text={t("Beta Feedback")}
                    className="p-1 m-0"
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Medium}
                    family={ETypographyFamily.DmSerif}
                    alignment={ETypographyAlignment.Center}
                    size={ETypographySize.Heading2}
                    tag={ETypographyTag.Heading2}
                />
            }
        >
            <DynamicHeightTransition>
                <section className="simulab-report-modal__body">
                    <CSSTransition
                        in={!feedbackSent}
                        timeout={600}
                        classNames="simulab-report-modal__form-animation"
                        onExit={handleFormExit}
                        nodeRef={feedbackModalRef}
                        unmountOnExit
                    >
                        <div>
                            <SimulabFeedbackForm report={report} user={user} onSubmit={onSubmit} />
                            <WarningMessage message={error} />
                        </div>
                    </CSSTransition>
                    <CSSTransition
                        in={showFeedbackAnimation}
                        timeout={600}
                        classNames="simulab-report-modal__feedback-animation"
                        nodeRef={feedbackResultRef}
                        unmountOnExit
                    >
                        <div className="text-center fs-sm-14">
                            <p className="m-0 text-gray-100 font-extrabold">{t("Thank you for your feedback!")}</p>
                            <p className="mb-[40px] text-gray-100">
                                {t("It helps us improve the experience for everyone.")}
                            </p>
                            <Button className="mx-auto" onClick={onDoneFeedbackClick}>
                                {t("Done")}
                            </Button>
                        </div>
                    </CSSTransition>
                </section>
            </DynamicHeightTransition>
        </Modal>
    )
}
